import { z } from 'zod';
import { AssignmentStatus } from './Assignment.js';

// Inventory model
// Inventory Item
// a specific item of inventory
// has a unique identifier in our system
// has specs that describe what product it is — this should work with CanonVehicle variants so it's perhaps just a productId and variantId that tells us which CanonVehicle variant this item is
// current owner is a specific ServicesProvider or OEM in the Ekho system. For now use ServicesProvider ids and we can create a new ServicesProvider for OEMs if needed
// we should also track history of ownership so that we can use this information to understand how to correctly generate sales documents like the MSO
// came from a specific inventory management system (perhaps allow for multiple sources in-case the same item is present in multiple systems)
// -- we should have a way to know which systems this item is present in and understand how this item is identified by each of those systems
// -- for now the available systems are lightspeed and shopify
// we need to know the status of the item in several ways
// online/offline sale
// -- has it been marked for online sale?
// assignment status (which should include details about which placedOrder, purchasedVehicle etc. the assignment is associated with)
// -- has it been soft locked?
// -- has it been hard locked?
// -- has it been sold?
// space for some other inventory ids like SKU
// space for other optional details about the vehicle product that should mirror some details we store on the CanonVehicle variant but which should really be vehicle-specific
// -- VIN
// -- model year
// -- odometer reading
// -- engine number
// -- color
// ============================================================================
// Enums
// ============================================================================
/**
 * Inventory data source
 */
const InventoryDataSource = {
    DMS: 'DMS',
    WEBSITE: 'WEBSITE',
    OEM: 'OEM',
    EKHO: 'EKHO',
};
const inventoryDataSourceValues = [
    InventoryDataSource.DMS,
    InventoryDataSource.WEBSITE,
    InventoryDataSource.OEM,
    InventoryDataSource.EKHO,
];
const inventoryDataSourceSchema = z.enum(inventoryDataSourceValues);
/**
 * Sync status for tracking scraping job health and data freshness
 */
const SyncStatus = {
    SYNCED: 'SYNCED',
    NEEDS_REVIEW: 'NEEDS_REVIEW',
    FIX_REQUIRED: 'FIX_REQUIRED',
    IN_PROGRESS: 'IN_PROGRESS',
};
const syncStatusValues = [
    SyncStatus.SYNCED,
    SyncStatus.NEEDS_REVIEW,
    SyncStatus.FIX_REQUIRED,
    SyncStatus.IN_PROGRESS,
];
const syncStatusSchema = z.enum(syncStatusValues);
/**
 * Inventory listing status enum
 *
 * - PUBLISHED: Listing is live on the website and visible to buyers
 * - HIDDEN: Listing is temporarily hidden from the website but can be re-published
 * - DRAFT: Listing is ready to publish (has pricing and at least one image) but not yet live
 * - INCOMPLETE_DRAFT: Listing is missing required basics (e.g., no images) and cannot be published
 *   Note: INCOMPLETE_DRAFT is NOT tied to canonVehicle matching status. A listing can be published
 *   without a matched canonVehicle as long as it has valid pricing and at least one image.
 */
var InventoryListingStatus;
(function (InventoryListingStatus) {
    InventoryListingStatus["PUBLISHED"] = "PUBLISHED";
    InventoryListingStatus["HIDDEN"] = "HIDDEN";
    InventoryListingStatus["DRAFT"] = "DRAFT";
    InventoryListingStatus["INCOMPLETE_DRAFT"] = "INCOMPLETE_DRAFT";
    InventoryListingStatus["LOCKED"] = "LOCKED";
    InventoryListingStatus["ARCHIVED"] = "ARCHIVED";
})(InventoryListingStatus || (InventoryListingStatus = {}));
/**
 * Inventory matching status enum
 */
var InventoryMatchingStatus;
(function (InventoryMatchingStatus) {
    InventoryMatchingStatus["RAW"] = "RAW";
    InventoryMatchingStatus["ENRICHED"] = "ENRICHED";
    InventoryMatchingStatus["OEM_MATCHED"] = "OEM_MATCHED";
    InventoryMatchingStatus["VEHICLE_MATCHED"] = "VEHICLE_MATCHED";
    // ACCESSORIES_MATCHED = 'ACCESSORIES_MATCHED',
    // COLOR_MATCHED = 'COLOR_MATCHED',
    // VARIANT_MATCHED = 'VARIANT_MATCHED',
    InventoryMatchingStatus["MATCHED"] = "MATCHED";
})(InventoryMatchingStatus || (InventoryMatchingStatus = {}));
/**
 * Automated canon vehicle build status enum
 * Tracks the status of automated canon vehicle creation triggered when no matching canon vehicle exists
 */
var AutomatedCanonVehicleBuildStatus;
(function (AutomatedCanonVehicleBuildStatus) {
    AutomatedCanonVehicleBuildStatus["IN_PROGRESS_CANON_VEHICLE_CREATION"] = "IN_PROGRESS_CANON_VEHICLE_CREATION";
    AutomatedCanonVehicleBuildStatus["FAILED_CANON_VEHICLE_CREATION"] = "FAILED_CANON_VEHICLE_CREATION";
    AutomatedCanonVehicleBuildStatus["FAILED_POST_BUILD_MATCHING"] = "FAILED_POST_BUILD_MATCHING";
    AutomatedCanonVehicleBuildStatus["SUCCESS"] = "SUCCESS";
    AutomatedCanonVehicleBuildStatus["FAILED_VARIANT_CREATION"] = "FAILED_VARIANT_CREATION";
})(AutomatedCanonVehicleBuildStatus || (AutomatedCanonVehicleBuildStatus = {}));
/**
 * Inventory item condition enum
 */
var InventoryItemCondition;
(function (InventoryItemCondition) {
    InventoryItemCondition["NEW"] = "N";
    InventoryItemCondition["USED"] = "U";
    InventoryItemCondition["CONSIGNED"] = "C";
    InventoryItemCondition["UNDEFINED"] = "X";
})(InventoryItemCondition || (InventoryItemCondition = {}));
var LaborPrepType;
(function (LaborPrepType) {
    LaborPrepType["SETUP"] = "S";
    LaborPrepType["INSTALL"] = "I";
    LaborPrepType["WARRANTY"] = "W";
})(LaborPrepType || (LaborPrepType = {}));
var PartsPrepType;
(function (PartsPrepType) {
    PartsPrepType["SETUP"] = "S";
    PartsPrepType["INSTALL"] = "I";
    PartsPrepType["NO_LABOR_ATTACHED"] = "N";
})(PartsPrepType || (PartsPrepType = {}));
/**
 * Inventory item use type enum
 */
var InventoryItemUseType;
(function (InventoryItemUseType) {
    InventoryItemUseType["STREET_LEGAL"] = "Street Legal";
    InventoryItemUseType["OFF_ROAD"] = "Off-Road";
})(InventoryItemUseType || (InventoryItemUseType = {}));
var InventoryItemMetric;
(function (InventoryItemMetric) {
    InventoryItemMetric["LAST_24_HOURS_VIEWS"] = "LAST_24_HOURS_VIEWS";
})(InventoryItemMetric || (InventoryItemMetric = {}));
class InventoryItem {
    constructor(data) {
        this.privateId = data.id;
        this.privateProductId = data.productId;
        this.privateVariantId = data.variantId;
        this.privateOwnerId = data.ownerId;
        this.privatePrimarySource = data.primarySource;
        this.privateSources = data.sources;
        this.privateIsMarkedForOnlineSale = data.isMarkedForOnlineSale;
        this.privateAssignment = data.assignment;
        this.privateExternalAssignment = data.externalAssignment;
        this.privateStockNumber = data.stockNumber;
        this.privateVehicleDetailsUrl = data.vehicleDetailsUrl;
        this.privateVin = data.vin;
        this.privateMake = data.make;
        this.privateModel = data.model;
        this.privateModelYear = data.modelYear;
        this.privateVehicleCategory = data.vehicleCategory;
        this.privateVehicleType = data.vehicleType;
        this.privateOdometerReading = data.odometerReading;
        this.privateOdometerUnit = data.odometerUnit;
        this.privateEngineNumber = data.engineNumber;
        this.privateVariantImageUrl = data.variantImageUrl;
        this.privateColor = data.color;
        this.privateBaseColor = data.baseColor;
        this.privateSpecs = data.specs;
        this.privateOwnershipHistory = data.ownershipHistory;
        this.privateDateAddedToInventory = data.dateAddedToInventory;
        this.privatePrioritizedForSale = data.prioritizedForSale;
        this.privateMetadata = data.metadata;
        this.privateexternalWebsiteId = data.externalWebsiteId;
        this.privatePricing = data.pricing;
        this.privateOemId = data.oemId;
        this.privateFieldSources = data.fieldSources;
        this.privateSyncStatus = data.syncStatus;
        this.privateInventoryOwnerAddress = data.inventoryOwnerAddress;
        this.privateGeoloc = data._geoloc;
        this.privateSyncStatusResolvedAt = data.syncStatusResolvedAt;
        this.privateListing = data.listing;
        this.privateDescription = data.description;
        this.privateSpecs = data.specs;
        this.privateImageUrls = data.imageUrls;
        this.privateFullName = data.fullName;
        this.privateScrapingConfig = data.scrapingConfig;
        this.privateDateCreated = data.dateCreated;
        this.privateDateUpdated = data.dateUpdated;
        this.privateDateAssigned = data.dateAssigned;
        this.privateDateDeleted = data.dateDeleted;
        this.privateImportJobId = data.importJobId;
        this.privateRaw = data.raw;
        this.privateMatching = data.matching;
        this.privateMetrics = data.metrics;
        this.privateMechanicalReport = data.mechanicalReport;
        this.privateCosmeticReport = data.cosmeticReport;
    }
    // Getters
    get id() {
        return this.privateId;
    }
    get productId() {
        return this.privateProductId;
    }
    get variantId() {
        return this.privateVariantId;
    }
    get ownerId() {
        return this.privateOwnerId;
    }
    get variantImageUrl() {
        return this.privateVariantImageUrl;
    }
    get primarySource() {
        return this.privatePrimarySource;
    }
    get sources() {
        return this.privateSources;
    }
    get inventoryOwnerAddress() {
        return this.privateInventoryOwnerAddress;
    }
    get geoloc() {
        return this.privateGeoloc;
    }
    get isMarkedForOnlineSale() {
        return this.privateIsMarkedForOnlineSale;
    }
    get assignment() {
        return this.privateAssignment;
    }
    get externalAssignment() {
        return this.privateExternalAssignment;
    }
    get stockNumber() {
        return this.privateStockNumber;
    }
    get vehicleDetailsUrl() {
        return this.privateVehicleDetailsUrl;
    }
    get vin() {
        return this.privateVin;
    }
    get make() {
        return this.privateMake;
    }
    get model() {
        return this.privateModel;
    }
    get modelYear() {
        return this.privateModelYear;
    }
    get odometerReading() {
        return this.privateOdometerReading;
    }
    get odometerUnit() {
        return this.privateOdometerUnit;
    }
    get engineNumber() {
        return this.privateEngineNumber;
    }
    get color() {
        return this.privateColor;
    }
    get baseColor() {
        return this.privateBaseColor;
    }
    get vehicleCategory() {
        return this.privateVehicleCategory;
    }
    get vehicleType() {
        return this.privateVehicleType;
    }
    get specs() {
        return this.privateSpecs;
    }
    get fees() {
        return this.privatePricing.fees;
    }
    get costs() {
        return this.privatePricing.costs;
    }
    get ownershipHistory() {
        return this.privateOwnershipHistory;
    }
    get dateAddedToInventory() {
        return this.privateDateAddedToInventory;
    }
    get prioritizedForSale() {
        return this.privatePrioritizedForSale;
    }
    get metadata() {
        return this.privateMetadata;
    }
    get externalWebsiteId() {
        return this.privateexternalWebsiteId;
    }
    get dateCreated() {
        return this.privateDateCreated;
    }
    get dateUpdated() {
        return this.privateDateUpdated;
    }
    get dateAssigned() {
        return this.privateDateAssigned;
    }
    get dateDeleted() {
        return this.privateDateDeleted;
    }
    get importJobId() {
        return this.privateImportJobId;
    }
    get pricing() {
        return this.privatePricing;
    }
    get fieldSources() {
        return this.privateFieldSources;
    }
    get syncStatus() {
        return this.privateSyncStatus;
    }
    get syncStatusResolvedAt() {
        return this.privateSyncStatusResolvedAt;
    }
    get listing() {
        return this.privateListing;
    }
    get description() {
        return this.privateDescription;
    }
    get raw() {
        return this.privateRaw;
    }
    get matching() {
        return this.privateMatching;
    }
    get metrics() {
        return this.privateMetrics;
    }
    get imageUrls() {
        return this.privateImageUrls;
    }
    get fullName() {
        return this.privateFullName;
    }
    get scrapingConfig() {
        return this.privateScrapingConfig;
    }
    get mechanicalReport() {
        return this.privateMechanicalReport;
    }
    get cosmeticReport() {
        return this.privateCosmeticReport;
    }
    // Methods for generating IDs
    static generateProductId(model, year) {
        return `${model}${year}`.replace(/\s+/g, '');
    }
    static generateVariantId(model, year, specs) {
        return `${this.generateProductId(model, year)}-${Object.values(specs).sort().join('-')}`;
    }
    // convert class instance to a plain object of type IInventoryItem
    toObject() {
        return {
            id: this.privateId,
            productId: this.privateProductId,
            variantId: this.privateVariantId,
            ownerId: this.privateOwnerId,
            primarySource: this.privatePrimarySource,
            sources: this.privateSources,
            isMarkedForOnlineSale: this.privateIsMarkedForOnlineSale,
            assignment: this.privateAssignment,
            externalAssignment: this.privateExternalAssignment,
            stockNumber: this.privateStockNumber,
            vehicleDetailsUrl: this.privateVehicleDetailsUrl,
            vin: this.privateVin,
            make: this.privateMake,
            model: this.privateModel,
            vehicleCategory: this.privateVehicleCategory,
            vehicleType: this.privateVehicleType,
            modelYear: this.privateModelYear,
            odometerReading: this.privateOdometerReading,
            odometerUnit: this.privateOdometerUnit,
            engineNumber: this.privateEngineNumber,
            color: this.privateColor,
            baseColor: this.privateBaseColor,
            specs: this.privateSpecs,
            ownershipHistory: this.privateOwnershipHistory,
            dateAddedToInventory: this.privateDateAddedToInventory,
            prioritizedForSale: this.privatePrioritizedForSale,
            metadata: this.privateMetadata,
            externalWebsiteId: this.privateexternalWebsiteId,
            dateCreated: this.privateDateCreated,
            dateUpdated: this.privateDateUpdated,
            dateAssigned: this.privateDateAssigned,
            dateDeleted: this.privateDateDeleted,
            importJobId: this.privateImportJobId,
            pricing: this.privatePricing,
            fieldSources: this.privateFieldSources,
            syncStatus: this.privateSyncStatus,
            syncStatusResolvedAt: this.privateSyncStatusResolvedAt,
            listing: this.privateListing,
            description: this.privateDescription,
            raw: this.privateRaw,
            matching: this.privateMatching,
            imageUrls: this.privateImageUrls,
            fullName: this.privateFullName,
            scrapingConfig: this.privateScrapingConfig,
            inventoryOwnerAddress: this.privateInventoryOwnerAddress,
            _geoloc: this.privateGeoloc,
            metrics: this.privateMetrics,
            mechanicalReport: this.privateMechanicalReport,
            cosmeticReport: this.privateCosmeticReport,
        };
    }
    updateAssignment(assignment) {
        this.privateAssignment = assignment;
    }
    updateDateAssigned(date) {
        this.privateDateAssigned = date;
    }
    static isItemAssignmentDetailsInSync(item) {
        // Helper function to check if two values are equal, treating undefined and null as equal
        const areEqual = (a, b) => {
            // If both are null or undefined, they're equal
            if ((a === null || a === undefined) && (b === null || b === undefined)) {
                return true;
            }
            // Otherwise do a direct comparison
            return a === b;
        };
        // Check all assignment details
        if (!areEqual(item.assignment?.details.placedOrderId, item.externalAssignment?.details.placedOrderId) ||
            !areEqual(item.assignment?.details.purchasedVehicleId, item.externalAssignment?.details.purchasedVehicleId) ||
            !areEqual(item.assignment?.details.checkoutCartId, item.externalAssignment?.details.checkoutCartId)) {
            return false;
        }
        return true;
    }
    static isItemAssignmentInSync(item) {
        let result = true;
        // First check if both assignment statuses match
        if (item.assignment.status !== item.externalAssignment.status) {
            result = false;
        }
        // Check all assignment details
        if (!InventoryItem.isItemAssignmentDetailsInSync(item)) {
            result = false;
        }
        return result;
    }
    static isLockValid(item, lockRequest) {
        const { assignment } = lockRequest;
        // const { filters } = lockRequest;
        if (!InventoryItem.isItemAssignmentInSync(item)) {
            return {
                allowed: false,
                reason: 'Assignment details are not in sync',
            };
        }
        // // match is allowed if the item is not marked for online sale but we are searching for a VIN match
        // if (!item.isMarkedForOnlineSale && !filters.vin) {
        // 	return {
        // 		allowed: false,
        // 		reason: 'Inventory item is not marked for online sale',
        // 	};
        // }
        // Only hard lock, soft lock, or sold are allowed as valid assignment statuses for locking
        if (![
            AssignmentStatus.HARD_LOCKED,
            AssignmentStatus.SOFT_LOCKED,
            AssignmentStatus.SOLD,
        ].includes(assignment.status)) {
            return {
                allowed: false,
                reason: `Invalid assignment status for locking: ${assignment.status}`,
            };
        }
        // If item is available, locking is allowed
        if (item.assignment.status === AssignmentStatus.AVAILABLE) {
            return { allowed: true };
        }
        // Check if this is an upgrade scenario
        const isUpgradingFromSoftLock = [AssignmentStatus.HARD_LOCKED, AssignmentStatus.SOLD].includes(assignment.status) &&
            item.assignment.status === AssignmentStatus.SOFT_LOCKED;
        const isUpgradingFromHardLock = assignment.status === AssignmentStatus.SOLD &&
            item.assignment.status === AssignmentStatus.HARD_LOCKED;
        const isKeepingLock = item.assignment.status === assignment.status;
        if (!(isUpgradingFromSoftLock || isUpgradingFromHardLock || isKeepingLock)) {
            return {
                allowed: false,
                reason: `Cannot change assignment status from ${item.assignment.status} to ${assignment.status}.`,
            };
        }
        // Check if either order IDs or cart ID match
        const orderIdsMatch = item.assignment.details.placedOrderId !== undefined &&
            item.assignment.details.placedOrderId !== null &&
            item.assignment.details.placedOrderId === assignment.details.placedOrderId &&
            item.assignment.details.purchasedVehicleId !== undefined &&
            item.assignment.details.purchasedVehicleId !== null &&
            item.assignment.details.purchasedVehicleId === assignment.details.purchasedVehicleId;
        const cartIdMatches = item.assignment.details.checkoutCartId !== undefined &&
            item.assignment.details.checkoutCartId !== null &&
            item.assignment.details.checkoutCartId === assignment.details.checkoutCartId;
        if (!(orderIdsMatch || cartIdMatches)) {
            return {
                allowed: false,
                reason: 'Order IDs or cart ID do not match for lock upgrade.',
            };
        }
        // All validation passed, this is a valid upgrade
        return {
            allowed: true,
            isUpgrade: isUpgradingFromSoftLock || isUpgradingFromHardLock,
            isIdentical: isKeepingLock,
        };
    }
    static isUnlockValid(item) {
        if (!InventoryItem.isItemAssignmentInSync(item)) {
            return {
                allowed: false,
                reason: 'Assignment details are not in sync',
            };
        }
        if ([AssignmentStatus.UNAVAILABLE, AssignmentStatus.AVAILABLE].includes(item.assignment.status)) {
            return {
                allowed: true,
                isUpgrade: false,
                isIdentical: true,
            };
        }
        if (item.assignment.status === AssignmentStatus.SOLD) {
            return {
                allowed: false,
                reason: `Inventory item with id ${item.id} is not available for unlocking`,
            };
        }
        return {
            allowed: true,
            isUpgrade: false,
            isIdentical: false,
        };
    }
    static getPricing(item) {
        return item.pricing;
    }
    static getFees(item) {
        return item.pricing.fees;
    }
    static getCosts(item) {
        return item.pricing.costs;
    }
}

export { AutomatedCanonVehicleBuildStatus, InventoryDataSource, InventoryItem, InventoryItemCondition, InventoryItemMetric, InventoryItemUseType, InventoryListingStatus, InventoryMatchingStatus, LaborPrepType, PartsPrepType, SyncStatus, inventoryDataSourceSchema, inventoryDataSourceValues, syncStatusSchema, syncStatusValues };
//# sourceMappingURL=InventoryItem.js.map
